/* global google */

import React, { Component } from "react";

import { findDOMNode } from "react-dom";

import * as R from "ramda";

import { graphql } from "react-apollo";
import gql from "graphql-tag";
import debounce from "debounce";

import { translate } from "react-i18next";

import { getDay } from "date-fns";

import { withGeoPosition, Media } from "react-fns";

import { Box, Button, Flex, Input, Link, A, Text } from "src/components";

import { theme } from "src/styles";
import { defaultPrevented } from "src/utils";

import { compose, withProps, withState, withHandlers } from "recompose";

import LocationList from "./LocationList";
import MapControl from "./MapControl";

import LocationAddress from "./LocationAddress";

import { getLocationType } from "./types";

import {
  GoogleMap,
  InfoWindow,
  Marker,
  withGoogleMap,
  withScriptjs,
} from "react-google-maps";

import scrollIntoView from "scroll-into-view";

import { StandaloneSearchBox } from "react-google-maps/lib/components/places/StandaloneSearchBox";

const locationToPoint = ({ latitude, longitude }) => ({
  lat: latitude,
  lng: longitude,
});

const enhanceInfoWindow = compose(
  withState("hoursExpanded", "setHoursExpanded", false)
);

const LocationInfoWindow = enhanceInfoWindow(
  ({ location, hoursExpanded, onCloseClick, setHoursExpanded, t }) => (
    <InfoWindow onCloseClick={onCloseClick}>
      <Box>
        <Text color="blue" fontWeight="bold">
          {location.name}
        </Text>
        <Text color="muted" fontSize={2}>
          {getLocationType(location, t).label}
        </Text>
        <LocationAddress {...location} />
      </Box>
    </InfoWindow>
  )
);

const filters = [
  {
    key: "fcfcu",
    label: "Financial Center Branches and ATMs",
    options: {
      branches: true,
      atms: true,
    },
  },
  {
    key: "all",
    label: "All Locations",
    emptyResultsLabel: "locations",
    options: {
      branches: true,
      atms: true,
      networkBranches: true,
      networkAtms: true,
    },
  },
  {
    key: "co-op",
    label: "CO-OP Shared Branches",
    options: {
      networkBranches: true,
    },
  },
  {
    key: "atms",
    label: "Surcharge-Free ATMs*",
    options: {
      networkAtms: true,
    },
  },
];

const getFilter = key => R.find(R.propEq("key", key), filters);

const LocationFilter = ({
  filter,
  handleChangeFilter,
  showShared,
  setShowShared,
}) => (
  <Flex flexDirection="column">
    <Flex flexDirection={["column", "column", "column", "row"]} mt={2}>
      {filters.map(({ key, label }) => (
        <Button
          flex={[undefined, undefined, undefined, 1]}
          onClick={() => {
            handleChangeFilter(key);
          }}
          key={key}
          bg={key === filter ? "rgba(255,255,255,0.2)" : "transparent"}
          hover={{
            backgroundColor: "rgba(255,255,255,0.1)",
          }}
          borderWidth={0}
          p={1}
          py={2}
          color="white"
          style={{ cursor: "pointer" }}
          align="center"
          justify="center"
          textAlign="center"
        >
          <Text color="white" fontSize={2}>
            {label}
          </Text>
        </Button>
      ))}
    </Flex>
  </Flex>
);

const NoLocations = ({ filter, ...props }) => {
  const currentFilter = getFilter(filter);

  return (
    <Flex p={3} flexDirection="column" align="center">
      <Text align="center">
        We couldn't find any{" "}
        {currentFilter.emptyResultsLabel || currentFilter.label} in this area.
      </Text>
      {props.zoom !== 1 && (
        <Button
          flex={1}
          alignSelf="stretch"
          onClick={defaultPrevented(props.handleZoomOut)}
          bg="green"
          borderRadius={5}
          mt={2}
          hover={{
            backgroundColor: "transparentGreen",
          }}
          borderWidth={0}
          p={1}
          py={1}
          color="white"
          style={{ cursor: "pointer" }}
          align="center"
          justify="center"
          textAlign="center"
        >
          <Text fontSize={2} fontWeight="semibold">
            Expand search
          </Text>
        </Button>
      )}
      {filter !== "all" && (
        <Text mt={2} flexDirection="row" fontSize={2} display="inline">
          <A
            href="#"
            onClick={e => {
              e.preventDefault();
              props.handleChangeFilter("all");
            }}
            color="green"
            fontSize="inherit"
          >
            show all locations
          </A>
        </Text>
      )}
    </Flex>
  );
};

const getLocationIcon = (location, t) => {
  const url = getLocationType(location, t).pinImage;

  return (
    url && {
      url,
      scaledSize: new google.maps.Size(22, 40),
    }
  );
};

class Map extends Component {
  constructor() {
    super();
    this.onMove = debounce(this.onMove, 100);
  }

  getMapBounds() {
    const bounds = this.map.getBounds();

    return bounds && bounds.toJSON();
  }

  onMove = () => {
    if (this.map) {
      if (this.props.bounds) {
        this.props.setDisplayedBounds(this.getMapBounds());
        if (this.props.ignoreMove) {
          this.props.setIgnoreMove(false);
        } else {
          this.props.setMoved(true);
        }
      } else {
        this.updateBounds();
      }
    }
  };

  updateBounds = () => {
    const bounds = this.getMapBounds();

    if (bounds) {
      this.props.setBounds(bounds);
    }

    this.props.setPage(0);
    this.props.setMoved(false);
  };

  updateZoom = e => {
    const zoom = this.map.getZoom();
    this.props.setZoom(zoom);
  };

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.focusedLocation &&
      nextProps.focusedLocation.id !== R.prop("id", this.props.focusedLocation)
    ) {
      if (this.props.scrollOnClick) {
        const node = findDOMNode(this.map);
        scrollIntoView(node, {
          align: {
            top: 0,
          },
        });
      }
      this.map.panTo(locationToPoint(nextProps.focusedLocation));
    }

    if (nextProps.coords && !this.props.coords) {
      this.props.setIgnoreMove(true);
      this.map.panTo(locationToPoint(nextProps.coords));
      this.updateBounds();
    }
  }

  render() {
    const {
      locations = [],
      currentLocationId,
      focusedLocation,
      setCurrentLocationId,
      innerRef,
      focusLocation,
      defaultZoom,
      moved,
      zoom,
      loading,
      t,
    } = this.props;

    const anyHovered = !!currentLocationId;

    return (
      <GoogleMap
        ref={map => {
          this.map = map;
          if (innerRef) {
            innerRef(map);
          }
        }}
        defaultZoom={defaultZoom}
        defaultCenter={this.props.defaultCenter}
        onBoundsChanged={this.onMove}
        controls={this.insertButton}
        options={{
          mapTypeControl: false,
        }}
        zoom={zoom}
        onZoomChanged={this.updateZoom}
      >
        {moved && (
          <MapControl
            controlPosition={google && google.maps.ControlPosition.TOP_CENTER}
          >
            <Link
              bg="white"
              px={2}
              py={1}
              mt={2}
              color="orange"
              fontWeight="bold"
              textDecoration="none"
              boxShadow={2}
              to="#"
              onClick={defaultPrevented(this.updateBounds)}
            >
              SEARCH THIS AREA
            </Link>
          </MapControl>
        )}
        {!loading &&
          locations.map(location => {
            const hovered = currentLocationId === location.id;

            return (
              <Marker
                key={location.id}
                icon={getLocationIcon(location, t)}
                position={locationToPoint(location)}
                onClick={() => focusLocation(location)}
                opacity={anyHovered && !hovered ? 0.5 : 1}
              >
                {location.id === R.prop("id", focusedLocation) && (
                  <LocationInfoWindow
                    onCloseClick={() => {
                      setCurrentLocationId(undefined);
                      focusLocation(undefined);
                    }}
                    t={t}
                    location={location}
                  />
                )}
              </Marker>
            );
          })}
      </GoogleMap>
    );
  }
}

// const enhanceMap = compose(withState("zoom", "setZoom", 8), withGoogleMap);

Map = withGoogleMap(Map);

class Locator extends Component {
  onPlacesChanged = () => {
    const place = R.prop(0, this.searchBox.getPlaces());

    if (place) {
      const bounds = place.geometry.viewport || place.geometry.location;
      this.props.setIgnoreMove(true);
      this.map.fitBounds(bounds);
      this.props.setBounds(bounds.toJSON());
    }
  };

  fitToLocations = locations => {
    const locationsToFit = locations || this.props.locations;

    if (locationsToFit) {
      const bounds = new google.maps.LatLngBounds();

      locationsToFit.forEach(location =>
        bounds.extend({
          lat: location.latitude,
          lng: location.longitude,
        })
      );

      this.props.setIgnoreMove(true);
      this.props.setMoved(false);
      this.map.fitBounds(bounds);
    }
  };

  focusLocation = location => {
    this.props.setIgnoreMove(true);
    if (location) {
      const southWest = new google.maps.LatLng(
        location.latitude - 0.005,
        location.longitude - 0.005
      );
      const northEast = new google.maps.LatLng(
        location.latitude + 0.005,
        location.longitude + 0.005
      );

      const bounds = new google.maps.LatLngBounds(southWest, northEast);

      this.map.fitBounds(bounds);
    } else {
      this.fitToLocations();
    }

    this.props.focusLocation(location);
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.locations && !this.props.locations) {
      // this.fitToLocations(nextProps.locations);
    }
  }

  render() {
    const props = this.props;
    const anyFocused = !!props.focusedLocation;
    const mobileMapHeight = anyFocused ? 300 : 500;
    const loading = props.data ? props.data.loading : true;
    const noLocations = !loading && R.isEmpty(props.locations);

    return (
      <Box mb={[3, 3, 3, 0]}>
        <StandaloneSearchBox
          ref={searchBox => (this.searchBox = searchBox)}
          onPlacesChanged={this.onPlacesChanged}
        >
          <Flex flexDirection="column" bg="blue">
            <Flex p={2} pb={0}>
              <Input
                flex={1}
                py={2}
                px={3}
                borderRadius={5}
                borderWidth={0}
                borderColor="none"
                placeholder='e.g. "123 Main St, Anytown, State", "Anytown", or "Zipcode"'
              />
            </Flex>
            <Box>
              <LocationFilter
                filter={props.filter}
                handleChangeFilter={props.handleChangeFilter}
                showShared={props.showShared}
                setShowShared={props.setShowShared}
              />
            </Box>
          </Flex>
        </StandaloneSearchBox>

        <Flex
          flexDirection={[
            "column-reverse",
            "column-reverse",
            "column-reverse",
            "row",
          ]}
          height={[undefined, undefined, undefined, 500]}
        >
          {noLocations ? (
            <Box flex={[undefined, undefined, undefined, 1]}>
              <NoLocations {...props} />
            </Box>
          ) : (
            <LocationList
              loading={loading}
              locations={props.locations}
              currentLocationId={props.currentLocationId}
              setCurrentLocationId={props.setCurrentLocationId}
              focusLocation={this.focusLocation}
              focusedLocation={props.focusedLocation}
              renderEmpty={this.renderEmpty}
              page={props.page}
              setPage={props.setPage}
              pageSize={props.pageSize}
              pageCount={props.pageCount}
              totalLocationCount={props.totalLocationCount}
              flex={1}
              t={props.t}
            />
          )}
          <Flex
            flex={[undefined, undefined, undefined, 3]}
            height={[mobileMapHeight, mobileMapHeight, mobileMapHeight, "auto"]}
          >
            <Media query={`(max-width: ${theme.breakpoints[2]}em)`}>
              {isMobile => (
                <Map
                  {...props}
                  innerRef={map => (this.map = map)}
                  scrollOnClick={isMobile}
                  loading={loading}
                  t={props.t}
                />
              )}
            </Media>
          </Flex>
        </Flex>
      </Box>
    );
  }
}

const locationsQuery = gql`
  query($locationInput: LocationQueryInput!) {
    locations(query: $locationInput) {
      id
      name
      latitude
      longitude
      shared
      hasBranch
      mapIconInfo
      address {
        line1
        line2
        city
        state
        zip
      }
      hours {
        day
        hours
      }
    }
  }
`;

const boundsToPoints = bounds => ({
  northeastPoint: {
    latitude: bounds.north,
    longitude: bounds.east,
  },
  southwestPoint: {
    latitude: bounds.south,
    longitude: bounds.west,
  },
});

const mapIndexed = R.addIndex(R.map);

const buildDirectionsUrl = R.pipe(
  R.props(["line1", "line2", "city", "state", "zip"]),
  R.reject(R.isNil),
  encodeURIComponent,
  R.concat("http://maps.google.com/maps?f=d&daddr=")
);

const getHoursForToday = location => {
  const todayIndex = getDay(new Date());
  return R.path(["hours", todayIndex, "hours"], location);
};

const transformLocations = locations =>
  mapIndexed((location, index) => {
    const letter = index < 26 ? String.fromCharCode(65 + index) : undefined;

    return R.pipe(
      R.assoc("letter", letter),
      R.assoc("zIndex", locations.length - index),
      R.assoc("directionsUrl", buildDirectionsUrl(location.address)),
      R.assoc(
        "hoursToday",
        Boolean(location.hours) && getHoursForToday(location)
      )
    )(location);
  })(locations);

const filterInputProps = filter => {
  const currentFilter = R.find(R.propEq("key", filter), filters);
  return currentFilter ? currentFilter.options : {};
};

const pageSize = 20;

const enhance = compose(
  withProps(props => {
    const apiKey = R.pathOr(10, ["options", "apiKey"], props);

    return {
      googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=3.exp&libraries=geometry,drawing,places`,
      loadingElement: <div style={{ height: `100%` }} />,
      containerElement: <Flex flex={1} />,
      mapElement: <div style={{ flex: 1 }} />,
      defaultCenter: locationToPoint(props.options.defaultLocation),
      defaultZoom: R.pathOr(10, ["options", "defaultLocation", "zoom"], props),
    };
  }),
  withScriptjs,
  // withGeoPosition,
  withState("filter", "setFilter", "fcfcu"),
  withState("page", "setPage", 0),
  withState("showShared", "setShowShared", true),
  withState("currentLocationId", "setCurrentLocationId"),
  withState("focusedLocation", "focusLocation"),
  withState("moved", "setMoved", false),
  withState("displayedBounds", "setDisplayedBounds"),
  withState("bounds", "setBounds"),
  withState("ignoreMove", "setIgnoreMove", false),
  withState("zoom", "setZoom", R.prop("defaultZoom")),
  translate("translations"),
  graphql(locationsQuery, {
    skip: props => !props.bounds,
    options: props =>
      props.bounds && {
        variables: {
          locationInput: {
            ...boundsToPoints(props.bounds),
            ...filterInputProps(props.filter),
            zoom: props.zoom,
          },
        },
      },
  }),
  withProps(props => {
    const locations = R.propOr([], "locations", props.data);

    const pageCount = Math.ceil(locations.length / pageSize);

    return {
      pageCount,
      pageSize,
      totalLocationCount: locations.length,
      locations: R.pipe(
        transformLocations,
        R.drop(props.page * pageSize),
        R.take(pageSize)
      )(locations),
    };
  }),
  withHandlers({
    handleChangeFilter: props => filter => {
      props.setFilter(filter);
      props.setMoved(false);
      props.setPage(0);
    },
    handleZoomOut: props => () => {
      props.setIgnoreMove(true);
      props.setZoom(props.zoom - 1);
      props.setBounds(props.displayedBounds);
      props.setPage(0);
    },
  })
);

export default enhance(Locator);



// WEBPACK FOOTER //
// ./src/locator/Locator.js